import { FileTypeEnum, SimulationDocumentDto } from 'api/documents';
import { BranchCode } from 'api/risk';
import { documentLabels } from 'shared/constants/documentLabels';
import { ContractualizationAction } from './actions';
import { ContractualizationState } from './types';

export const initialState: ContractualizationState = {
  quoteData: null,
  documents: [],
  isFetchingDocuments: false,
  isSendingContract: false,
  error: null
};

export const contractualizationReducer = (
  state: ContractualizationState,
  action: ContractualizationAction
): ContractualizationState => {
  switch (action.type) {
    case 'SET_QUOTE_DATA':
      return { ...state, quoteData: action.payload };

    case 'SET_DOCUMENTS':
      return { ...state, documents: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_IS_FETCHING_DOCUMENTS':
      return { ...state, isFetchingDocuments: action.payload };

    case 'SET_IS_SENDING_CONTRACT':
      return { ...state, isSendingContract: action.payload };

    case 'UPDATE_DOCUMENT_FROM_NOTIFICATION': {
      const notification = action.payload;
      if (notification.storedFileId !== null) {
        const targetFileType =
          notification.replaceFileType || notification.fileType;
        if (targetFileType) {
          const docIndex = state.documents.findIndex(
            doc => doc.fileType === targetFileType
          );
          if (docIndex !== -1) {
            const newDocuments = [...state.documents];
            newDocuments[docIndex] = {
              ...newDocuments[docIndex],
              ...notification,
              fileType: notification.fileType || newDocuments[docIndex].fileType
            };
            return { ...state, documents: newDocuments };
          } else if (notification.fileType) {
            return {
              ...state,
              documents: [
                ...state.documents,
                {
                  fileType: notification.fileType,
                  label: notification.label || '',
                  storedFileId: notification.storedFileId
                }
              ]
            };
          }
        }
      }
      return state;
    }

    case 'ADD_CONTRACT_DOCUMENT': {
      const { hasGroupContract } = action.payload;
      const hasContractDoc = state.documents.some(
        doc =>
          doc.fileType === FileTypeEnum.BulletinAdhesion ||
          doc.fileType === FileTypeEnum.BulletinAdhesionSigne
      );

      if (!hasContractDoc) {
        return {
          ...state,
          documents: [
            ...state.documents,
            {
              fileType: FileTypeEnum.BulletinAdhesion,
              label: hasGroupContract
                ? 'DOCUMENT.LABELS.GC_CERTIFICATE'
                : 'DOCUMENT.LABELS.INSURANCE_PROPOSAL'
            }
          ]
        };
      }
      return state;
    }

    case 'SET_FETCHED_DOCUMENTS': {
      const { branchCode, documents } = action.payload;

      // The notice is the general conditions document; RGI products also carry special conditions.
      const branchFileTypes: FileTypeEnum[] = branchCode === BranchCode.RGI
        ? [FileTypeEnum.ConditionsSpeciales]
        : [];

      const allowedDocTypes: FileTypeEnum[] = [
        FileTypeEnum.BulletinAdhesion,
        FileTypeEnum.SimulationComplete,
        FileTypeEnum.AdviceSheetPdfGenerer,
        FileTypeEnum.BulletinAdhesionSigne,
        FileTypeEnum.CertificatAdhesion,
        FileTypeEnum.Ipid,
        FileTypeEnum.Notice,
        ...branchFileTypes,
        FileTypeEnum.QuittanceGenerer
      ];

      const filteredDocs: SimulationDocumentDto[] = documents
        .filter(doc => allowedDocTypes.includes(doc.fileType))
        .map(doc => ({
          ...doc,
          label: doc.label || ''
        }));

      // Generated documents get a placeholder line while they are produced. Product
      // documents (IPID, notice) only show when uploaded, i.e. returned by the back:
      // a placeholder would spin forever on a product that has none.
      const defaultDocs = [
        {
          fileType: FileTypeEnum.SimulationComplete,
          label: documentLabels[FileTypeEnum.SimulationComplete]
        },
        {
          fileType: FileTypeEnum.AdviceSheetPdfGenerer,
          label: documentLabels[FileTypeEnum.AdviceSheetPdfGenerer]
        }
      ];

      const existingTypes = new Set(filteredDocs.map(doc => doc.fileType));

      const finalDocs = [
        ...filteredDocs,
        ...defaultDocs.filter(
          defaultDoc => !existingTypes.has(defaultDoc.fileType)
        )
      ];

      return { ...state, documents: finalDocs };
    }

    case 'RESET':
      return initialState;

    default:
      return state;
  }
};
